/* eslint-disable @typescript-eslint/naming-convention */
import type { CollectionEventSystemConfig, RewardConfig, RewardConfigToCalculate } from './types';
import { PredefinedVisualType, VisualConfigType } from './types';
import { TileColor } from '../../game/boardMVC/TileColor';
import { TimeLimitType } from '../../libs/timeLimit/types';
import { MS_IN_DAY } from '../../utils/time-utils';

const rewards: (RewardConfig | RewardConfigToCalculate)[] = [
  {
    rewards: { BoosterRedraw: 1 },
    tokensReq: 50,
  },
  {
    rewards: { UnlimitedLivesMinutes: 30 },
    tokensReq: 100,
  },
  {
    rewards: { Coin: 50 },
    tokensReq: null,
    calculation: {
      median: true,
      medianMultiplier: 3,
    },
  },
  {
    rewards: { BoosterMove: 1 },
    tokensReq: null,
    calculation: {
      median: true,
      medianMultiplier: 3,
    },
  },
  {
    rewards: { UnlimitedLivesMinutes: 30 },
    tokensReq: null,
    calculation: {
      median: true,
      medianMultiplier: 4,
    },
  },
  {
    rewards: { Coin: 50 },
    tokensReq: null,
    calculation: {
      median: true,
      medianMultiplier: 6,
    },
  },
  {
    rewards: { UnlimitedLivesMinutes: 60 },
    tokensReq: null,
    calculation: {
      median: true,
      medianMultiplier: 5,
    },
  },
  {
    rewards: { Coin: 50 },
    tokensReq: null,
    calculation: {
      median: true,
      medianMultiplier: 7,
    },
  },
  {
    rewards: { Coin: 100 },
    tokensReq: null,
    calculation: {
      median: true,
      medianMultiplier: 9,
    },
  },
  {
    rewards: { BoosterUndo: 1 },
    tokensReq: null,
    calculation: {
      median: true,
      medianMultiplier: 7,
    },
  },
  {
    rewards: { UnlimitedLivesMinutes: 60 },
    tokensReq: null,
    calculation: {
      median: true,
      medianMultiplier: 9,
    },
  },
  {
    rewards: { Coin: 100 },
    tokensReq: null,
    calculation: {
      median: true,
      medianMultiplier: 11,
    },
  },
  {
    rewards: { BoosterRedraw: 1 },
    tokensReq: null,
    calculation: {
      median: true,
      medianMultiplier: 9,
    },
  },
  {
    rewards: { UnlimitedLivesMinutes: 120 },
    tokensReq: null,
    calculation: {
      median: true,
      medianMultiplier: 11,
    },
  },
  {
    rewards: { Coin: 100 },
    tokensReq: null,
    calculation: {
      median: true,
      medianMultiplier: 13,
    },
  },
  {
    rewards: { UnlimitedLivesMinutes: 120 },
    tokensReq: null,
    calculation: {
      median: true,
      medianMultiplier: 11,
    },
  },
  {
    rewards: { Coin: 100 },
    tokensReq: null,
    calculation: {
      median: true,
      medianMultiplier: 13,
    },
  },
  {
    rewards: { BoosterHammer: 1 },
    tokensReq: null,
    calculation: {
      median: true,
      medianMultiplier: 15,
    },
  },
  {
    rewards: { Coin: 100 },
    tokensReq: null,
    calculation: {
      median: true,
      medianMultiplier: 13,
    },
  },
  {
    rewards: { UnlimitedLivesMinutes: 120 },
    tokensReq: null,
    calculation: {
      median: true,
      medianMultiplier: 15,
    },
  },
  {
    rewards: { Coin: 100 },
    tokensReq: null,
    calculation: {
      median: true,
      medianMultiplier: 17,
    },
  },
  {
    rewards: { BoosterRedraw: 1 },
    tokensReq: null,
    calculation: {
      median: true,
      medianMultiplier: 15,
    },
  },
  {
    rewards: { Coin: 200 },
    tokensReq: null,
    calculation: {
      median: true,
      medianMultiplier: 17,
    },
  },
  {
    rewards: { UnlimitedLivesMinutes: 180 },
    tokensReq: null,
    calculation: {
      median: true,
      medianMultiplier: 19,
    },
  },
  {
    rewards: { Coin: 500 },
    tokensReq: null,
    calculation: {
      median: true,
      medianMultiplier: 21,
    },
  },
];

const base = {
  tempTokens: 0,
  tokensCollected: 0,
  tokensLastSeen: 0,
  priority: false,
  rewardsLoop: { start: 11, end: 23 },
  rewards,
};

export const collectionEventConfig: CollectionEventSystemConfig = {
  regularEvents: [
    {
      ...base,
      id: 'regularLeaf',
      title: {
        en_US: 'Green Woods',
        it_IT: 'Boschi Verdi',
        fr_FR: 'Bois Verdoyants',
        de_DE: 'Grüne Wälder',
        es_ES: 'Bosques Verdes',
        pt_PT: 'Florestas Verdes',
        pl_PL: 'Zielone Lasy',
        nl_NL: 'Groene Wouden',
        ko_KR: '푸른 숲',
        ja_JP: '緑の森',
      },
      tokenType: [TileColor.Green],
      description: {
        text: {
          en_US: 'Clear %GREEN% stacks to collect $LEAVES$ and win rewards!',
          it_IT: 'Pulisci le pile %VERDI% per raccogliere $FOGLIE$ e ottenere ricompense!',
          fr_FR: 'Effacez les piles %VERTES% pour collecter des $FEUILLES$ et gagner des récompenses !',
          de_DE: 'Räume die %GRÜNEN% Stapel ab, um $BLÄTTER$ zu sammeln und Belohnungen zu gewinnen!',
          es_ES: 'Limpia las pilas %VERDES% para recoger $HOJAS$ y ganar recompensas.',
          pt_PT: 'Limpa as pilhas %VERDES% para recolher $FOLHAS$ e ganhar recompensas!',
          pl_PL: 'Usuń %ZIELONE% stosy, aby zebrać $LIŚCIE$ i zdobyć nagrody!',
          nl_NL: 'Ruim de %GROENE% stapels op om $BLADEREN$ te verzamelen en beloningen te winnen!',
          ko_KR: '%초록색% 스택을 제거하여 $잎사귀$를 모으고 보상을 받으세요!',
          ja_JP: '%緑色%のスタックをクリアして $ハッパ$ を集め、報酬をゲットしよう！',
        },
        colorTags: {
          $: '#FFD200',
          '%': '#16FC00',
        },
      },
      timeLimit: {
        type: TimeLimitType.Repeating,
        startDate: Date.UTC(2025, 11, 29, 12, 0),
        duration: MS_IN_DAY * 7,
        toLocalTime: true,
        repeatsEvery: MS_IN_DAY * 7 * 4,
      },
      visuals: {
        configType: VisualConfigType.Predefined,
        visualType: PredefinedVisualType.Leaf,
      },
    },
    {
      ...base,
      id: 'regularUnicorn',
      title: {
        en_US: 'Sparkle Time',
        it_IT: 'Bagliore Mistico',
        fr_FR: 'Éclat Mystique',
        de_DE: 'Mystischer Glanz',
        es_ES: 'Brillo Místico',
        pt_PT: 'Brilho Místico',
        pl_PL: 'Mistyczny Blask',
        nl_NL: 'Mystieke Gloed',
        ko_KR: '신비한 빛',
        ja_JP: '神秘の輝き',
      },
      tokenType: [TileColor.Purple],
      description: {
        text: {
          en_US: 'Clear %PURPLE% stacks to collect $UNICORNS$ and win rewards!',
          it_IT: 'Pulisci le pile %VIOLA% per raccogliere $UNICORNI$ e vincere ricompense!',
          fr_FR: 'Effacez les piles %VIOLETTES% pour collecter des $LICORNES$ et gagner des récompenses !',
          de_DE: 'Räume die %LILANEN% Stapel ab, um $EINHÖRNER$ zu sammeln und Belohnungen zu gewinnen!',
          es_ES: 'Limpia las pilas %MORADAS% para recoger $UNICORNIOS$ y ganar recompensas.',
          pt_PT: 'Limpa as pilhas %ROXAS% para recolher $UNICÓRNIOS$ e ganhar recompensas!',
          pl_PL: 'Usuń %FIOLETOWE% stosy, aby zebrać $JEDNOROŻCE$ i zdobyć nagrody!',
          nl_NL: 'Maak de %PAARSE% stapels leeg om $EENHOORNS$ te verzamelen en beloningen te winnen!',
          ko_KR: '%보라색% 스택을 제거하여 $유니콘$을 모으고 보상을 받으세요!',
          ja_JP: '%紫色%のスタックをクリアして $ユニコーン$ を集め、報酬をゲットしよう！',
        },
        colorTags: {
          $: '#FFD200',
          '%': '#B120FF',
        },
      },
      timeLimit: {
        type: TimeLimitType.Repeating,
        startDate: Date.UTC(2026, 0, 5, 12, 0),
        duration: MS_IN_DAY * 7,
        toLocalTime: true,
        repeatsEvery: MS_IN_DAY * 7 * 4,
      },
      visuals: {
        configType: VisualConfigType.Predefined,
        visualType: PredefinedVisualType.Unicorn,
      },
    },
    {
      ...base,
      id: 'regularBee',
      title: {
        en_US: 'Hive Harvest',
        it_IT: 'Raccolta del Miele',
        fr_FR: 'Récolte du Miel',
        de_DE: 'Honigernte',
        es_ES: 'Cosecha de Miel',
        pt_PT: 'Colheita de Mel',
        pl_PL: 'Miodobranie',
        nl_NL: 'Honingoogst',
        ko_KR: '꿀 수확',
        ja_JP: '蜂蜜の収穫',
      },
      tokenType: [TileColor.Yellow],
      description: {
        text: {
          en_US: 'Clear $YELLOW$ stacks to collect $BEES$ and win rewards!',
          it_IT: 'Pulisci le pile $GIALLE$ per raccogliere $API$ e vincere ricompense!',
          fr_FR: 'Effacez les piles $JAUNES$ pour collecter des $ABEILLES$ et gagner des récompenses !',
          de_DE: 'Räume die $GELBEN$ Stapel ab, um $BIENEN$ zu sammeln und Belohnungen zu gewinnen!',
          es_ES: 'Limpia las pilas $AMARILLAS$ para recoger $ABEJAS$ y ganar recompensas.',
          pt_PT: 'Limpa as pilhas $AMARELAS$ para recolher $ABELHAS$ e ganhar recompensas!',
          pl_PL: 'Usuń $ŻÓŁTE$ stosy, aby zebrać $PSZCZOŁY$ i zdobyć nagrody!',
          nl_NL: 'Maak de $GELE$ stapels leeg om $BIJEN$ te verzamelen en beloningen te winnen!',
          ko_KR: '$노란$ 스택을 제거하여 $꿀벌$을 모으고 보상을 받으세요!',
          ja_JP: '$黄色$のスタックをクリアして $ハチ$ を集め、報酬をゲットしよう！',
        },
        colorTags: {
          $: '#FFD200',
        },
      },
      timeLimit: {
        type: TimeLimitType.Repeating,
        startDate: Date.UTC(2026, 0, 12, 12, 0),
        duration: MS_IN_DAY * 7,
        toLocalTime: true,
        repeatsEvery: MS_IN_DAY * 7 * 4,
      },
      visuals: {
        configType: VisualConfigType.Predefined,
        visualType: PredefinedVisualType.Bee,
      },
    },
    {
      ...base,
      id: 'regularFlame',
      title: {
        en_US: 'Fired Forge',
        it_IT: 'Forgia Ardente',
        fr_FR: 'Forge Ardente',
        de_DE: 'Feurige Schmiede',
        es_ES: 'Forja Ardiente',
        pt_PT: 'Forja Ardente',
        pl_PL: 'Ognista Kuźnia',
        nl_NL: 'Vurige Smederij',
        ko_KR: '불타는 대장간',
        ja_JP: '炎の鍛冶場',
      },
      tokenType: [TileColor.Red],
      description: {
        text: {
          en_US: 'Clear %RED% stacks to collect $FLAMES$ and win rewards!',
          it_IT: 'Pulisci le pile %ROSSE% per raccogliere $FIAMME$ e ottenere ricompense!',
          fr_FR: 'Effacez les piles %ROUGES% pour collecter des $FLAMMES$ et gagner des récompenses !',
          de_DE: 'Räume die %ROTEN% Stapel ab, um $FLAMMEN$ zu sammeln und Belohnungen zu gewinnen!',
          es_ES: 'Limpia las pilas %ROJAS% para recoger $LLAMAS$ y ganar recompensas.',
          pt_PT: 'Limpa as pilhas %VERMELHAS% para recolher $CHAMAS$ e ganhar recompensas!',
          pl_PL: 'Usuń %CZERWONE% stosy, aby zebrać $PŁOMIENIE$ i zdobyć nagrody!',
          nl_NL: 'Ruim de %RODE% stapels op om $VLAMMEN$ te verzamelen en beloningen te winnen!',
          ko_KR: '%빨간색% 스택을 제거하여 $불꽃$를 모으고 보상을 받으세요!',
          ja_JP: '%赤色%のスタックをクリアして $ホノオ$ を集め、報酬をゲットしよう！',
        },
        colorTags: {
          $: '#FFD200',
          '%': '#FF2517',
        },
      },
      timeLimit: {
        type: TimeLimitType.Repeating,
        startDate: Date.UTC(2026, 0, 19, 12, 0),
        duration: MS_IN_DAY * 7,
        toLocalTime: true,
        repeatsEvery: MS_IN_DAY * 7 * 4,
      },
      visuals: {
        configType: VisualConfigType.Predefined,
        visualType: PredefinedVisualType.Flame,
      },
    },
  ],
  priorityEvents: [],
};
